/**
 * Minimal stale-while-revalidate cache for page data.
 *
 * Purpose: make RETURNING to a page (Dashboard, College Lists, a school modal)
 * feel instant without changing results — cached data paints immediately, the
 * normal fetch still runs and replaces it, so the user always converges on
 * exactly what they'd have seen without the cache. Never used to SKIP a fetch,
 * only to paint sooner.
 *
 * In-memory Map (fast path, survives route changes within the SPA) backed by
 * sessionStorage (survives reloads, per-tab, cleared on tab close). Values are
 * JSON-serializable API responses. TTL only bounds how stale a first paint can
 * be — expired entries are ignored, not returned.
 */

const mem = new Map<string, { v: unknown; t: number }>();
const PREFIX = 'swr:';

export function swrGet<T>(key: string, maxAgeMs: number): T | null {
  const now = Date.now();
  const m = mem.get(key);
  if (m && now - m.t <= maxAgeMs) return m.v as T;
  try {
    const raw = sessionStorage.getItem(PREFIX + key);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as { v: T; t: number };
    if (now - parsed.t > maxAgeMs) return null;
    mem.set(key, parsed);
    return parsed.v;
  } catch {
    return null;
  }
}

export function swrSet(key: string, value: unknown): void {
  const entry = { v: value, t: Date.now() };
  mem.set(key, entry);
  try {
    sessionStorage.setItem(PREFIX + key, JSON.stringify(entry));
  } catch {
    /* quota/serialization failures just mean no persisted cache */
  }
}

/**
 * Invalidate cached entries (BUG_LIST #152): after a mutation the cached list
 * counts are stale, so returning to a page paints wrong numbers before the
 * refetch lands. Pass an exact key, or a predicate to match a family of keys
 * (e.g. all `dashSchools:*` entries), to drop them so the next paint is fresh.
 */
export function swrInvalidate(match: string | ((key: string) => boolean)): void {
  const predicate = typeof match === 'string' ? (k: string) => k === match : match;
  // eslint-disable-next-line @typescript-eslint/no-array-constructor
  const keys = Array.from(mem.keys());
  keys.forEach((k) => {
    if (predicate(k)) mem.delete(k);
  });
  try {
    for (let i = sessionStorage.length - 1; i >= 0; i--) {
      const storageKey = sessionStorage.key(i);
      if (storageKey && storageKey.startsWith(PREFIX)) {
        const bare = storageKey.slice(PREFIX.length);
        if (predicate(bare)) sessionStorage.removeItem(storageKey);
      }
    }
  } catch {
    /* sessionStorage may be unavailable */
  }
}
